import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

(() => {
  'use strict';

  const section = document.querySelector('[data-wishlist-page]');
  if (!section) return;

  const grid = section.querySelector('[data-wishlist-grid]');
  const empty = section.querySelector('[data-wishlist-empty]');
  const loading = section.querySelector('[data-wishlist-loading]');
  const count = section.querySelector('[data-wishlist-page-count]');
  const root = section.dataset.rootUrl || '/';
  const currency = section.dataset.currency || 'CAD';
  const cache = new Map();
  let renderNumber = 0;

  const route = (path) => `${root.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  const productUrl = (handle, variantId) => {
    const url = route(`products/${encodeURIComponent(handle)}`);
    return variantId ? `${url}?variant=${encodeURIComponent(variantId)}` : url;
  };

  const imageSource = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const imageUrl = (source, width) => {
    const url = imageSource(source);
    if (!url) return '';
    try {
      const image = new URL(url, window.location.origin);
      image.searchParams.set('width', String(width));
      return image.toString();
    } catch (_error) {
      return url;
    }
  };

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return `${currency} ${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text === 'string') node.textContent = text;
    return node;
  };

  const variantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options.map(String);
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null).map(String);
  };

  const optionDefinitions = (product) => {
    const options = Array.isArray(product.options) ? product.options : [];
    return options.map((option, index) => {
      const name = typeof option === 'string' ? option : option.name || `Option ${index + 1}`;
      const suppliedValues = typeof option === 'string' ? [] : option.values || [];
      const values = suppliedValues.length
        ? suppliedValues.map((value) => String(value.value || value))
        : [...new Set((product.variants || []).map((variant) => variantOptions(variant)[index]).filter(Boolean))];
      return { name, values };
    });
  };

  const createRemoveButton = (product) => {
    const button = element('button', 'ns-wishlist-card__remove', section.dataset.removeText || 'REMOVE');
    button.type = 'button';
    button.dataset.wishlistToggle = '';
    button.dataset.productHandle = product.handle;
    button.dataset.labelAdd = (section.dataset.addLabel || '').replace('__TITLE__', product.title);
    button.dataset.labelRemove = (section.dataset.removeLabel || '').replace('__TITLE__', product.title);
    button.setAttribute('aria-label', button.dataset.labelRemove);
    button.setAttribute('aria-pressed', 'true');
    return button;
  };

  const updatePrice = (container, variant) => {
    container.replaceChildren(document.createTextNode(money(variant?.price)));
    if (Number(variant?.compare_at_price) > Number(variant?.price)) {
      container.append(element('span', 'ns-wishlist-card__compare', money(variant.compare_at_price)));
    }
  };

  const addToBag = async (variant, product, source) => {
    const sectionIds = [...new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean)
    )];
    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = 1;

    source.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variant.id), quantity: itemCount }],
      promise: deferred.promise,
    }));

    try {
      const payload = { items: [{ id: Number(variant.id), quantity: itemCount }] };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const response = await fetch(window.Theme?.routes?.cart_add_url || route('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || section.dataset.addError);
      }

      const cartResponse = await fetch(window.Theme?.routes?.cart_url
        ? `${window.Theme.routes.cart_url}.json`
        : route('cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!cartResponse.ok) throw new Error(section.dataset.addError);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ns-wishlist',
          sourceId: source.id,
          itemCount,
          productId: String(product.id),
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      source.dispatchEvent(new CartErrorEvent({
        error: error?.message || section.dataset.addError,
        code: 'INVALID',
      }));
      deferred.reject(error);
      throw error;
    }
  };

  const createCard = (product, index) => {
    const card = element('article', 'ns-wishlist-card');
    const media = element('div', 'ns-wishlist-card__media');
    const mediaLink = element('a', 'ns-wishlist-card__media-link');
    const image = element('img', 'ns-wishlist-card__image');
    const details = element('div', 'ns-wishlist-card__details');
    const title = element('a', 'ns-wishlist-card__title', product.title);
    const price = element('p', 'ns-wishlist-card__price');
    const purchase = element('div', 'ns-wishlist-card__purchase');
    const controls = element('div', 'ns-wishlist-card__options');
    const status = element('p', 'ns-wishlist-card__status');
    const addButton = element('button', 'ns-wishlist-card__add', section.dataset.addToBag || 'ADD TO BAG');
    const view = element('a', 'ns-wishlist-card__view', section.dataset.viewProduct || 'VIEW PRODUCT');
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const selectedVariant = variants.find((variant) => variant.available) || variants[0] || null;
    const definitions = optionDefinitions(product);
    const selection = selectedVariant ? variantOptions(selectedVariant) : [];
    const selectNodes = [];
    const state = { variant: selectedVariant };

    mediaLink.href = productUrl(product.handle, selectedVariant?.id);
    mediaLink.setAttribute('aria-label', product.title);
    mediaLink.append(image);
    media.append(mediaLink, createRemoveButton(product));

    image.alt = product.title;
    image.width = 720;
    image.height = 720;
    image.loading = index < 4 ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.sizes = '(max-width: 749px) 50vw, 25vw';
    if (index < 4) image.fetchPriority = 'high';

    title.href = productUrl(product.handle, selectedVariant?.id);
    view.href = title.href;
    addButton.type = 'button';
    addButton.id = `WishlistAdd-${product.id || index}`;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const updateCard = () => {
      const variant = variants.find((candidate) => {
        const values = variantOptions(candidate);
        return selection.every((value, optionIndex) => values[optionIndex] === value);
      }) || null;
      state.variant = variant;
      const available = Boolean(variant?.available);
      const variantUrl = productUrl(product.handle, variant?.id);
      const source = imageSource(variant?.featured_image) || imageSource(product.featured_image);

      title.href = variantUrl;
      view.href = variantUrl;
      mediaLink.href = variantUrl;
      image.src = imageUrl(source, 720);
      image.srcset = source ? `${imageUrl(source, 360)} 360w, ${imageUrl(source, 720)} 720w` : '';
      image.hidden = !source;
      updatePrice(price, variant || product);
      addButton.disabled = !available;
      addButton.textContent = available
        ? section.dataset.addToBag || 'ADD TO BAG'
        : section.dataset.soldOut || 'SOLD OUT';
    };

    definitions.forEach((definition, optionIndex) => {
      const defaultTitle = /^title$/i.test(definition.name) && definition.values.length === 1;
      if (defaultTitle || definition.values.length < 2) return;

      const label = element('label', 'ns-wishlist-card__option');
      const labelText = element('span', 'ns-wishlist-card__option-label', definition.name);
      const select = element('select', 'ns-wishlist-card__select');
      const selectLabel = (section.dataset.selectOption || 'SELECT __OPTION__').replace('__OPTION__', definition.name);
      select.setAttribute('aria-label', selectLabel);

      definition.values.forEach((value) => {
        const option = element('option', '', value);
        option.value = value;
        option.selected = selection[optionIndex] === value;
        option.disabled = !variants.some((variant) => variant.available && variantOptions(variant)[optionIndex] === value);
        select.append(option);
      });

      select.addEventListener('change', () => {
        selection[optionIndex] = select.value;
        status.textContent = '';
        updateCard();
      });
      label.append(labelText, select);
      controls.append(label);
      selectNodes.push(select);
    });

    controls.classList.toggle('ns-wishlist-card__options--single', selectNodes.length === 1);
    controls.hidden = selectNodes.length === 0;

    addButton.addEventListener('click', async () => {
      if (!state.variant?.available || addButton.disabled) return;
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.textContent = section.dataset.adding || 'ADDING...';
      status.textContent = '';

      try {
        await addToBag(state.variant, product, addButton);
        addButton.textContent = section.dataset.added || 'ADDED';
        window.setTimeout(() => {
          if (!addButton.isConnected) return;
          addButton.disabled = false;
          addButton.removeAttribute('aria-busy');
          addButton.textContent = section.dataset.addToBag || 'ADD TO BAG';
        }, 1400);
      } catch (_error) {
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
        addButton.textContent = section.dataset.addToBag || 'ADD TO BAG';
        status.textContent = section.dataset.addError || 'UNABLE TO ADD. TRY AGAIN.';
      }
    });

    updateCard();
    purchase.append(controls, status, addButton, view);
    details.append(title, price, purchase);
    card.append(media, details);
    return card;
  };

  const createUnavailableCard = (handle) => {
    const titleText = section.dataset.unavailable || 'PRODUCT UNAVAILABLE';
    const product = { handle, title: titleText };
    const card = element('article', 'ns-wishlist-card');
    const media = element('div', 'ns-wishlist-card__media ns-wishlist-card__media--unavailable');
    const details = element('div', 'ns-wishlist-card__details');
    const title = element('p', 'ns-wishlist-card__title', titleText);
    const status = element('p', 'ns-wishlist-card__price', titleText);
    media.append(createRemoveButton(product));
    details.append(title, status);
    card.append(media, details);
    return card;
  };

  const getProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(handle, fetch(`${productUrl(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        })
        .catch(() => null));
    }
    return cache.get(handle);
  };

  const loadProducts = async (handles) => {
    const results = new Array(handles.length);
    let cursor = 0;
    const worker = async () => {
      while (cursor < handles.length) {
        const index = cursor++;
        results[index] = await getProduct(handles[index]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, handles.length) }, worker));
    return results;
  };

  const render = async () => {
    const currentRender = ++renderNumber;
    const handles = window.NSWishlist ? window.NSWishlist.get() : [];
    count.textContent = `(${handles.length})`;
    count.hidden = handles.length === 0;
    grid.replaceChildren();
    empty.hidden = true;

    if (handles.length === 0) {
      loading.hidden = true;
      empty.hidden = false;
      return;
    }

    loading.hidden = false;
    const products = await loadProducts(handles);
    if (currentRender !== renderNumber) return;

    const fragment = document.createDocumentFragment();
    products.forEach((product, index) => {
      fragment.append(product ? createCard(product, index) : createUnavailableCard(handles[index]));
    });
    grid.append(fragment);
    loading.hidden = true;
    if (window.NSWishlist) window.NSWishlist.sync();
  };

  const start = () => {
    render();
    window.addEventListener('ns:wishlist:change', render);
  };

  if (window.NSWishlist) start();
  else window.addEventListener('ns:wishlist:ready', start, { once: true });
})();